/**
 * BAASetupModal Component
 *
 * Modal for requesting a Business Associate Agreement (BAA).
 * Collects name, email, and entity name for the covered entity/practice.
 */

import { useEffect, useState } from 'react';
import { useUserInfo } from '@features/user';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

interface BAASetupModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: BAAFormData) => Promise<void>;
}

export interface BAAFormData {
  name: string;
  email: string;
  entityName: string;
}

interface FormState {
  name: string;
  email: string;
  entityName: string;
}

export function BAASetupModal({ open, onClose, onSubmit }: BAASetupModalProps) {
  const userInfo = useUserInfo();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const [form, setForm] = useState<FormState>({
    name: '',
    email: '',
    entityName: '',
  });

  // Prefill form when modal opens
  useEffect(() => {
    if (open) {
      setForm({
        name: userInfo?.name || '',
        email: userInfo?.email || '',
        entityName: '', // No prefill - user must enter
      });
      setError(null);
      setShowSuccess(false);
    }
  }, [open, userInfo?.name, userInfo?.email]);

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!form.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!form.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!/\S+@\S+\.\S+/.test(form.email.trim())) {
      setError('Please enter a valid email address');
      return;
    }
    if (!form.entityName.trim()) {
      setError('Entity name is required');
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      await onSubmit({
        name: form.name.trim(),
        email: form.email.trim(),
        entityName: form.entityName.trim(),
      });
      setShowSuccess(true);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to submit BAA request. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth={"sm"}>
      <DialogTitle>Request Business Associate Agreement</DialogTitle>
      <DialogContent>
        {showSuccess ? (
          <Box sx={{ py: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Your BAA request has been submitted successfully.
            </Alert>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              Active in 2-3 business days.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Our team will review your request and prepare the Business Associate Agreement. You
              will receive an email notification once it&apos;s ready.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              A HIPAA-aligned BAA built for behavioral-health workflows—clear, balanced, and designed
              to reduce legal friction. It includes strict limits on PHI use, clinic-owned data
              terms, incident timelines, and subcontractor protections.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              label="Name"
              fullWidth
              required
              value={form.name}
              onChange={handleChange('name')}
              disabled={isSubmitting}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Email"
              type="email"
              fullWidth
              required
              value={form.email}
              onChange={handleChange('email')}
              disabled={isSubmitting}
              sx={{ mb: 2 }}
            />

            <TextField
              label="Entity Name (Covered Entity / Practice)"
              fullWidth
              required
              value={form.entityName}
              onChange={handleChange('entityName')}
              disabled={isSubmitting}
              placeholder="Enter the legal name of your practice or organization"
              helperText="The covered entity that will be named in the BAA"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {showSuccess ? (
          <Button onClick={handleClose} variant="contained">
            Done
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {isSubmitting ? 'Submitting...' : 'Request BAA'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
